// Temporary stat modifiers (events such as Halloween) as the server sends them.
// /playertroop and /playeritem use camelCase; PvE battle reports and stored arena
// reports use the C# names (PascalCase), so every reader accepts both.

export type StatModifierStat = {
  stat: number;
  percent: number;
  original: number;
  value: number;
  currentOriginal?: number;
  currentValue?: number;
};

export type StatModifiers = {
  theme: string;
  stats: StatModifierStat[];
};

export type BattleOriginalStats = {
  damage?: number;
  armor?: number;
  magicResistance?: number;
  maxHealth?: number;
};

export type AlteredKind = "troop" | "item";

// Themes that have a translated label. A new theme needs its own keys in barracks.json.
export const ALTERED_THEMES = ["halloween"];

const HEALTH_STAT = 0;

function field(source: any, name: string) {
  if (!source || typeof source !== "object") return undefined;
  if (source[name] !== undefined) return source[name];
  return source[name.charAt(0).toUpperCase() + name.slice(1)];
}

function toNumber(value: any): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** The modifiers in camelCase, or undefined when there are none. */
export function normalizeStatModifiers(raw: any): StatModifiers | undefined {
  const rawStats = field(raw, "stats");
  if (!Array.isArray(rawStats)) return undefined;
  const stats: StatModifierStat[] = [];
  rawStats.forEach((line) => {
    const stat = toNumber(field(line, "stat"));
    const original = toNumber(field(line, "original"));
    const value = toNumber(field(line, "value"));
    if (stat === undefined || original === undefined || value === undefined) return;
    stats.push({
      stat,
      percent: toNumber(field(line, "percent")) ?? 0,
      original,
      value,
      currentOriginal: toNumber(field(line, "currentOriginal")),
      currentValue: toNumber(field(line, "currentValue")),
    });
  });
  if (stats.length === 0) return undefined;
  const theme = field(raw, "theme");
  return { theme: typeof theme === "string" ? theme : "", stats };
}

/** The modifiers of a troop or item object, whichever casing it uses. */
export function statModifiersOf(owner: any): StatModifiers | undefined {
  return normalizeStatModifiers(field(owner, "statModifiers"));
}

/** The line for one stat number (Health 0, Armor 1, Healing 3, MagicResistance 4, Damage 5). */
export function getStatLine(
  statModifiers: any,
  stat: number
): StatModifierStat | undefined {
  return normalizeStatModifiers(statModifiers)?.stats.find(
    (line) => line.stat === stat
  );
}

/** Whether the modifiers change anything, or the given stat when one is passed. */
export function isModified(statModifiers: any, stat?: number): boolean {
  const modifiers = normalizeStatModifiers(statModifiers);
  if (!modifiers) return false;
  return stat === undefined
    ? modifiers.stats.length > 0
    : modifiers.stats.some((line) => line.stat === stat);
}

/** The i18n key of the "altered" label, only for themes that have one. */
export function alteredLabelKey(
  theme: string | undefined,
  kind: AlteredKind
): string | undefined {
  if (!theme) return undefined;
  const normalized = theme.toLowerCase();
  if (!ALTERED_THEMES.includes(normalized)) return undefined;
  return `barracks:altered.${normalized}.${kind}`;
}

/** The theme of a modified troop or item that has a label, otherwise undefined. */
export function alteredThemeOf(owner: any): string | undefined {
  const modifiers = statModifiersOf(owner);
  if (!modifiers) return undefined;
  return alteredLabelKey(modifiers.theme, "troop")
    ? modifiers.theme.toLowerCase()
    : undefined;
}

/**
 * A troop's own stat as shown before a battle: the modified value and, while a
 * modifier runs, the original. Pass the troop field as `base`; it is the original.
 */
export function statPair(statModifiers: any, stat: number, base: any) {
  const line = getStatLine(statModifiers, stat);
  return line
    ? { value: line.value, original: line.original }
    : { value: base, original: undefined };
}

/**
 * Health as "current/max" for the modified value and, while a modifier runs, the
 * original. Pass the troop fields (the originals); without a modifier the value
 * is exactly "health/maxHealth" through the given formatters.
 */
export function healthPair(
  statModifiers: any,
  health: any,
  maxHealth: any,
  formatCurrent: (value: any) => any = (value) => value ?? "",
  formatMax: (value: any) => any = formatCurrent
) {
  const line = getStatLine(statModifiers, HEALTH_STAT);
  if (!line)
    return {
      value: `${formatCurrent(health)}/${formatMax(maxHealth)}`,
      original: undefined,
      direction: undefined,
    };
  const current = line.currentValue ?? health;
  const currentOriginal = line.currentOriginal ?? health;
  const direction: "up" | "down" | undefined =
    line.value > line.original ? "up" : line.value < line.original ? "down" : undefined;
  return {
    value: `${formatCurrent(current)}/${formatMax(line.value)}`,
    original: `${formatCurrent(currentOriginal)}/${formatMax(line.original)}`,
    direction,
  };
}

/** An item's bonus without the modifier; undefined when no modifier changes it. */
export function itemOriginalBonus(item: any): number | undefined {
  const modifiers = statModifiersOf(item);
  if (!modifiers) return undefined;
  const bonusStat = toNumber(field(field(item, "item"), "bonusStat"));
  const line =
    modifiers.stats.find((entry) => entry.stat === bonusStat) ??
    modifiers.stats[0];
  return line?.original;
}

/** The Barracks total of one stat: base, the Barracks percent bonus on it, and the item bonus. */
export function barracksStatTotal(
  base: number,
  barracksBonusPercent: number | undefined,
  itemValue: number
) {
  const bonusValue = !barracksBonusPercent
    ? 0
    : (base * barracksBonusPercent) / 100;
  return base + bonusValue + itemValue;
}

/**
 * The Barracks total of one stat had no modifier run, worked out with the same
 * formula from the originals. Undefined when neither the troop's stat nor any
 * of its items for that stat is modified.
 */
export function originalBarracksTotal(
  items: any[] | undefined,
  troopStatModifiers: any,
  troopValue: number,
  bonusStat: number,
  barracksBonusPercent: number | undefined
): number | undefined {
  const line = getStatLine(troopStatModifiers, bonusStat);
  let itemsModified = false;
  let itemOriginal = 0;
  (items || [])
    .filter((item) => field(field(item, "item"), "bonusStat") === bonusStat)
    .forEach((item) => {
      const original = itemOriginalBonus(item);
      if (original !== undefined) itemsModified = true;
      itemOriginal += original ?? (toNumber(field(item, "bonusValue")) || 0);
    });
  if (!line && !itemsModified) return undefined;
  return barracksStatTotal(
    line ? line.original : troopValue,
    barracksBonusPercent,
    itemOriginal
  );
}

/** The starting battle totals without modifiers, from a report entry or a StartBattle unit. */
export function originalStatsOf(owner: any): BattleOriginalStats | undefined {
  const raw = field(owner, "originalStats");
  if (!raw || typeof raw !== "object") return undefined;
  return {
    damage: toNumber(field(raw, "damage")),
    armor: toNumber(field(raw, "armor")),
    magicResistance: toNumber(field(raw, "magicResistance")),
    maxHealth: toNumber(field(raw, "maxHealth")),
  };
}

// The rules of the modifiers running now, as the battle server lists them
// (api/battle/statModifiers). The arena formation screen uses them to mark the
// troops and items a modifier changes; battles apply them on the server.
export type StatModifierRule = {
  theme?: string;
  targetType: number;
  targetId: number;
  minLevel?: number;
  maxLevel?: number;
  stat: number;
  percent: number;
};

export const MODIFIER_TARGET_TROOP = 5;
export const MODIFIER_TARGET_ITEM = 6;

// Translation keys of the stats a modifier can change (item bonus stat numbers).
export const MODIFIER_STAT_KEYS: Record<number, string> = {
  0: "barracks:stats.health",
  1: "barracks:stats.armor",
  3: "barracks:stats.healing",
  4: "barracks:stats.magicResistance",
  5: "barracks:stats.damage",
};

/** The rules in camelCase; anything malformed is left out. */
export function normalizeModifierRules(raw: any): StatModifierRule[] {
  if (!Array.isArray(raw)) return [];
  const rules: StatModifierRule[] = [];
  raw.forEach((rule) => {
    const targetType = toNumber(field(rule, "targetType"));
    const targetId = toNumber(field(rule, "targetId"));
    const stat = toNumber(field(rule, "stat"));
    const percent = toNumber(field(rule, "percent"));
    if (targetType === undefined || targetId === undefined || stat === undefined || !percent) return;
    rules.push({
      theme: field(rule, "theme") ?? undefined,
      targetType,
      targetId,
      minLevel: toNumber(field(rule, "minLevel")),
      maxLevel: toNumber(field(rule, "maxLevel")),
      stat,
      percent,
    });
  });
  return rules;
}

/**
 * The rules that change one troop or item. With a level, only rules whose level
 * range takes it; without one (a catalog entry), any rule for it.
 */
export function modifierRulesFor(
  rules: StatModifierRule[] | undefined,
  targetType: number,
  targetId: number | undefined,
  level?: number
): StatModifierRule[] {
  if (!rules?.length || targetId === undefined) return [];
  return rules.filter(
    (rule) =>
      rule.targetType === targetType &&
      rule.targetId === targetId &&
      (level === undefined ||
        ((rule.minLevel === undefined || level >= rule.minLevel) &&
          (rule.maxLevel === undefined || level <= rule.maxLevel)))
  );
}

/** "+50%", "−40%". */
export function formatModifierPercent(percent: number): string {
  const rounded = Math.round(percent * 100) / 100;
  return (rounded > 0 ? "+" : "−") + Math.abs(rounded) + "%";
}
